import {
  type ReplyComposeSeed,
  useEmailCompose,
} from "hooks/use-email-compose";
import { type FC, useEffect } from "react";
import { toast } from "react-toastify";
import { ReplyComposePane } from "routes/emails/EmailDetailPage/components/ReplyComposePane";
import { toastError } from "utils/toast-utils";

export type ReplyComposeModalProps = {
  isOpen: boolean;
  /** モーダル表示時に使う返信シード。開いた瞬間に draft を初期化する */
  seed: ReplyComposeSeed | null;
  onClose: () => void;
  /** 送信成功時のコールバック（呼び出し元でバッジ更新等を行う） */
  onSent?: () => void;
};

/**
 * 共通返信モーダル。Sales Board / 返信管理画面 / メール詳細 の 3 箇所から
 * 呼び出せるよう、useEmailCompose を内蔵しつつ ReplyComposePane をラップする。
 * @param {ReplyComposeModalProps} props
 * @return {JSX.Element|null}
 */
export const ReplyComposeModal: FC<ReplyComposeModalProps> = ({
  isOpen,
  seed,
  onClose,
  onSent,
}) => {
  const {
    draft,
    isGenerating,
    isSending,
    error,
    startReplyFromSeed,
    setField,
    addRecipient,
    removeRecipient,
    generateBody,
    sendEmail,
    resetDraft,
    cancelGeneration,
  } = useEmailCompose();

  useEffect(() => {
    if (isOpen && seed) {
      startReplyFromSeed(seed);
    }
    if (!isOpen) {
      resetDraft();
    }
  }, [isOpen, seed, startReplyFromSeed, resetDraft]);

  if (!isOpen || !draft) return null;

  /**
   * 送信実行
   * @return {Promise<boolean>} 成功したか
   */
  const handleSend = async (): Promise<boolean> => {
    const success = await sendEmail();
    if (success) {
      toast.success("返信メールを送信しました");
      onSent?.();
      onClose();
    } else {
      toastError("メールの送信に失敗しました");
    }
    return success;
  };

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-2xl p-0 bg-transparent shadow-none">
        <ReplyComposePane
          draft={draft}
          sourceEmailType={null}
          isGenerating={isGenerating}
          isSending={isSending}
          error={error}
          onFieldChange={setField}
          onAddRecipient={addRecipient}
          onRemoveRecipient={removeRecipient}
          onGenerateBody={generateBody}
          onSend={handleSend}
          onClose={onClose}
          onCancelGeneration={cancelGeneration}
        />
      </div>
      <div className="modal-backdrop" onClick={onClose} />
    </div>
  );
};
